import { useEffect } from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { agentSharingService } from "@/services/agentSharingService";
import { Copy, Check, Share2, X, FolderOpen } from "lucide-react";
import type { AgentFolderShare } from "@shared/schema";

interface ShareAgentFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderId: string;
  folderName: string;
  agentCount: number;
}

export function ShareAgentFolderDialog({
  open,
  onOpenChange,
  folderId,
  folderName,
  agentCount,
}: ShareAgentFolderDialogProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [accessType, setAccessType] = useState<"restricted" | "anyone" | "organization">("restricted");

  const { data: existingShare, refetch: refetchShare } = useQuery<AgentFolderShare | null>({
    queryKey: ["/api/agent-folders", folderId, "share"],
    queryFn: async () => agentSharingService.getFolderShareLink(folderId),
    enabled: open && !!folderId,
  });

  const createShareMutation = useMutation({
    mutationFn: async () => {
      return await agentSharingService.createFolderShareLink({
        folderId,
        accessType,
      });
    },
    onSuccess: () => {
      refetchShare();
      toast({
        title: "Share link created",
        description: "Your folder share link has been generated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create share link",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const revokeShareMutation = useMutation({
    mutationFn: async () => agentSharingService.revokeFolderShareLink(folderId),
    onSuccess: () => {
      refetchShare();
      toast({
        title: "Share link revoked",
        description: "The folder share link has been deactivated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to revoke share link",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const shareLink = existingShare
    ? `${window.location.origin}/agents/folders/share/${existingShare.shareToken}`
    : null;

  const handleCopyLink = async () => {
    if (!shareLink) {
      toast({
        title: "No link available",
        description: "Please create a share link first",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast({ title: "Link copied!", description: "Folder share link copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Failed to copy",
        description: "Please copy the link manually",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (existingShare) {
      setAccessType(
        (existingShare.accessType as "restricted" | "anyone" | "organization") || "restricted"
      );
    }
  }, [existingShare]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Agent Folder
          </DialogTitle>
          <DialogDescription>
            Share &quot;{folderName}&quot; ({agentCount} agent{agentCount !== 1 ? "s" : ""}) with your team via one link
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {agentCount === 0 ? (
            <p className="text-sm text-muted-foreground">
              Add at least one custom agent to this folder before sharing.
            </p>
          ) : !shareLink ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="folder-access-type">Access Type</Label>
                <Select value={accessType} onValueChange={(v) => setAccessType(v as typeof accessType)}>
                  <SelectTrigger id="folder-access-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restricted">Restricted (Invite Only)</SelectItem>
                    <SelectItem value="anyone">Anyone with Link</SelectItem>
                    <SelectItem value="organization">Organization Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => createShareMutation.mutate()}
                disabled={createShareMutation.isPending}
                className="w-full"
                data-testid="button-create-folder-share-link"
              >
                {createShareMutation.isPending ? "Creating..." : "Generate Share Link"}
              </Button>
              <p className="text-xs text-amber-600 dark:text-amber-500">
                Agent config (lens, output rules, training doc IDs) is copied to recipients.
                They may need to re-link Knowledge Base documents in their library.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Shareable Link</Label>
                <div className="flex items-center gap-2">
                  <Input value={shareLink} readOnly className="flex-1 font-mono text-sm" />
                  <Button variant="outline" size="icon" onClick={handleCopyLink}>
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <FolderOpen className="h-3 w-3" />
                  Recipients accept once to receive all agents in this folder
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  Training document IDs are copied with each agent. Recipients may need to re-link
                  Knowledge Base documents if they do not have access to your library items.
                </p>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => revokeShareMutation.mutate()}
                  disabled={revokeShareMutation.isPending}
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  Revoke Link
                </Button>
                <Button onClick={handleCopyLink} className="flex-1">
                  <Copy className="h-4 w-4 mr-2" />
                  {copied ? "Copied!" : "Copy Link"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}